import React, { useEffect, useRef, useState } from "react"

import { useRemirrorContext } from "@remirror/react"
// @ts-ignore
import { styled } from "styled-components"

// See: https://docs.remirror.org/guides/advanced-guide/
const InnerEditorWrapper = styled.div<{ $disableBorder: boolean }>`
  display: flex;

  .remirror-editor {
    cursor: text;
    box-sizing: border-box;
    position: relative;
    line-height: 1.6em;
    width: 100%;
    min-height: 25px !important;
    word-break: normal;
    overflow: hidden !important;
    overflow-wrap: anywhere;

    &:focus {
      outline: none;
    }

    &.ProseMirror-focused {
      .ProseMirror-menubar {
        display: none;
      }
    }

    &[contenteditable="false"] {
      box-shadow: 0px 0px 0px 0 !important;
      padding: 0;
    }

    &[contenteditable="true"] {
      padding: 10px;
      border-radius: var(--borderRadiusMedium, 5px);
      background-color: var(--colorNeutralBackground1, #fff);
      box-shadow: 0 0 0 ${({ $disableBorder }) => ($disableBorder ? 0 : 1)}px
        var(--colorNeutralStroke1, rgba(0, 0, 0, 0.1)) !important;
      min-height: 90px !important;
    }

    &[contenteditable="false"] {
      ul,
      ol {
        text-align: left;
      }

      .remirror-resizable-view > div {
        display: none !important;
      }
    }

    .remirror-resizable-view {
      max-width: var(--parent-width, 100%) !important;
    }

    img {
      max-width: var(--parent-width, 100%) !important;
    }

    h1 {
      font-size: 1.5rem;
    }

    h2 {
      font-size: 1.25rem;
    }

    p {
      margin: 0;
    }

    p em {
      letter-spacing: 1.2px;
    }

    a {
      text-decoration: none !important;
      color: ${(props) => (props.theme ? props.theme.primaryColor : "var(--colorBrandForegroundLink, #0078d4)")};
    }

    h1,
    h2,
    h3,
    h4,
    h5,
    p,
    a,
    span {
      font-family: "Inter", inherit;
    }

    .remirror-is-empty {
      font-size: small !important;
      font-weight: normal !important;
    }

    .ProseMirror-selectednode {
      background-color: rgb(245, 248, 250);
    }

    .mention-suggest {
      pointer-events: none;
      cursor: default;
      color: ${(props) => (props.theme ? props.theme.primaryColor : "var(--colorBrandForegroundLink, #0078d4)")};
    }

    .mention,
    .mention-suggest {
      cursor: pointer;
      color: ${(props) => (props.theme ? props.theme.primaryColor : "var(--colorBrandForegroundLink, #0078d4)")};
    }

    s > .selection {
      text-decoration: line-through;
    }

    span[data-ext="file"] {
      cursor: pointer;
      background-color: var(--colorNeutralBackground2, #e4e4e4);
      padding: 2px 4px;
      padding-right: 6px;
      border-radius: 2px;
    }

    .video-node-view-wrapper {
      display: inline-block;
    }

    span[data-node] {
      max-width: 98%;
      position: relative;
      min-width: 20px;
      min-height: 20px;
      display: inline-block;
      overflow: hidden;
      vertical-align: middle;

      &.mouseDown {
        > * {
          pointer-events: none;
        }
      }
      > * {
        object-fit: contain;
        width: 100%;
        height: 100%;
        display: inline-block;
      }
    }
  }
`

type IReactTextEditor = {
  disableBorder?: boolean
}

const ReactTextEditor = ({ disableBorder }: IReactTextEditor) => {
  const context = useRemirrorContext()
  const { getRootProps } = context
  const sizingRef = useRef()
  const [parentWidth, setParentWidth] = useState(0)

  // Not allowed to use dict spread for `key`
  const { key, ...props } = getRootProps()

  useEffect(() => {
    if (sizingRef.current) {
      const updateParentWidth = () => {
        const parent = sizingRef?.current?.parentElement
        if (parent) {
          setParentWidth(parent.clientWidth - 20)
        }
      }
      updateParentWidth()

      const listener = () => {
        updateParentWidth()
      }
      window.addEventListener("resize", listener)
      return () => {
        window.removeEventListener("resize", listener)
      }
    }
  }, [])

  // The remirror Link extension opens link after the onclick handler is executed so we need to capture the click event and prevent it
  // This allows us to display the external link warning before opening the link
  // https://github.com/remirror/remirror/issues/1597
  useEffect(() => {
    const captureClicks = (e) => {
      if (!(e.target instanceof Element)) return // ignore non-elements such as simulated clicks
      // By bypass the normal link following flow in remirror to add custom handling for external links (the external link modal code see in Remirror/index.tsx)
      if (e.target.matches(".ProseMirror.remirror-editor a[href]")) {
        e.preventDefault()
      }
    }

    window.addEventListener("click", captureClicks)
    return () => {
      window.removeEventListener("click", captureClicks)
    }
  }, [])

  return (
    <>
      <InnerEditorWrapper
        $disableBorder={disableBorder}
        style={{ "--parent-width": `${parentWidth}px` }}
        key={key}
        {...props}
      />
      <div ref={sizingRef} />
    </>
  )
}

export default React.memo(ReactTextEditor)
